export interface ViewState {
    scrollX: number;
    scrollY: number;
    zoom: number;
    viewportWidth: number;
    viewportHeight: number;
    selection: { col: number, row: number, endCol: number, endRow: number } | null;
    // Number of top rows that are "frozen" (pinned) and should not scroll vertically.
    // Used for Query-results header row (row 0).
    frozenRowsTop?: number;
    // Number of left columns that are "frozen" (pinned) and should not scroll horizontally.
    frozenColsLeft?: number;
    // Optional precomputed layout info (for performance). If provided,
    // renderers can avoid O(N) scans from row/col 0 on every frame.
    startCol?: number;
    endCol?: number;
    startRow?: number;
    endRow?: number;
    startX?: number; // pixel position of startCol's left edge (relative to viewport)
    startY?: number; // pixel position of startRow's top edge (relative to viewport)
    selectionScreenRect?: { x: number; y: number; w: number; h: number } | null;
    clipboardSelectionScreenRect?: { x: number; y: number; w: number; h: number } | null;
    clipboardMode?: 'copy' | 'cut' | null;
    dragPreviewScreenRect?: { x: number; y: number; w: number; h: number } | null;
    dragPreviewMode?: 'move' | 'copy' | 'fill' | null;
    marqueeOffset?: number;
    gridlinesVisible?: boolean;
    sheetProtection?: {
        enabled: boolean;
    } | null;
}

const getMergeRootKey = (
    col: number,
    row: number,
    format: {
        mergeAnchorRowOffset?: number,
        mergeAnchorColOffset?: number,
    } | undefined,
): string | null => {
    if (!format) return null;
    const rowOffset = Math.max(0, Math.floor(Number(format.mergeAnchorRowOffset) || 0));
    const colOffset = Math.max(0, Math.floor(Number(format.mergeAnchorColOffset) || 0));
    const rowSpan = Math.max(1, Math.floor(Number((format as any).mergeRowSpan) || 1));
    const colSpan = Math.max(1, Math.floor(Number((format as any).mergeColSpan) || 1));
    if (rowOffset <= 0 && colOffset <= 0 && rowSpan <= 1 && colSpan <= 1) return null;
    return `${row - rowOffset}:${col - colOffset}`;
};

const sharesMergeRoot = (
    leftCol: number,
    leftRow: number,
    leftFormat: any,
    rightCol: number,
    rightRow: number,
    rightFormat: any,
): boolean => {
    const leftKey = getMergeRootKey(leftCol, leftRow, leftFormat);
    const rightKey = getMergeRootKey(rightCol, rightRow, rightFormat);
    return Boolean(leftKey && rightKey && leftKey === rightKey);
};

const mergedPlacement = (format: any): { rowOffset: number; colOffset: number; rowSpan: number; colSpan: number } | null => {
    const rowOffset = Math.max(0, Math.floor(Number(format?.mergeAnchorRowOffset) || 0));
    const colOffset = Math.max(0, Math.floor(Number(format?.mergeAnchorColOffset) || 0));
    const rowSpan = Math.max(1, Math.floor(Number(format?.mergeRowSpan) || 1));
    const colSpan = Math.max(1, Math.floor(Number(format?.mergeColSpan) || 1));
    if (rowOffset <= 0 && colOffset <= 0 && rowSpan <= 1 && colSpan <= 1) return null;
    return { rowOffset, colOffset, rowSpan, colSpan };
};

const drawRectIfVisible = (
    ctx: CanvasRenderingContext2D,
    rect: { x: number; y: number; w: number; h: number },
    viewportWidth: number,
    viewportHeight: number,
    draw: () => void,
) => {
    if (rect.x >= viewportWidth || rect.x + rect.w <= 0 || rect.y >= viewportHeight || rect.y + rect.h <= 0) return;
    draw();
};

const styleSection = (format: any, key: string): any => (
    format?.[key] && typeof format[key] === 'object' && !Array.isArray(format[key])
        ? format[key]
        : {}
);

const conditionalSection = (format: any, key: string): any => (
    format?.[key] && typeof format[key] === 'object' && !Array.isArray(format[key])
        ? format[key]
        : null
);

const normalizeFillPatternKind = (raw: unknown): string => {
    const pattern = String(raw ?? '').trim();
    switch (pattern) {
        case 'darkGray':
            return 'gray75';
        case 'mediumGray':
            return 'gray50';
        case 'lightGray':
            return 'gray25';
        case 'darkHorizontal':
        case 'lightHorizontal':
            return 'horizontal';
        case 'darkVertical':
        case 'lightVertical':
            return 'vertical';
        case 'darkDown':
        case 'lightDown':
            return 'reverseDiagonal';
        case 'darkUp':
        case 'lightUp':
            return 'diagonal';
        case 'darkGrid':
        case 'lightGrid':
            return 'horizontalCross';
        case 'darkTrellis':
        case 'lightTrellis':
            return 'diagonalCross';
        default:
            return pattern;
    }
};

const fillPatternRenderSpec = (raw: unknown): { kind: string; step: number; alpha: number } => {
    const pattern = String(raw ?? '').trim();
    const kind = normalizeFillPatternKind(pattern);
    const lower = pattern.toLowerCase();
    const light = lower.startsWith('light') || lower === 'gray0625' || lower === 'gray125';
    const dark = lower.startsWith('dark') || lower === 'gray75' || lower === 'darkgray';
    const dense = lower === 'gray50' || lower === 'mediumgray';
    if (lower === 'gray0625') return { kind, step: 12, alpha: 0.18 };
    if (lower === 'gray125') return { kind, step: 10, alpha: 0.25 };
    if (light) return { kind, step: 9, alpha: 0.28 };
    if (dark) return { kind, step: 4, alpha: 0.7 };
    if (dense) return { kind, step: 5, alpha: 0.55 };
    if (kind === 'gray25') return { kind, step: 7, alpha: 0.38 };
    return { kind, step: 8, alpha: 0.42 };
};

const getCellBackgroundColor = (format: any): string | undefined => {
    const fill = styleSection(format, 'fill');
    const colorScale = conditionalSection(format, 'colorScale');
    const patternKind = normalizeFillPatternKind(fill.patternKind ?? fill.pattern);
    if (patternKind && patternKind !== 'none' && patternKind !== 'solid') {
        return colorScale?.color || fill.backgroundColor || format?.backgroundColor || '#ffffff';
    }
    return colorScale?.color || format?.backgroundColor || fill.foregroundColor || fill.backgroundColor;
};

const drawCellFill = (
    ctx: CanvasRenderingContext2D,
    format: any,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    const fill = styleSection(format, 'fill');
    const backgroundColor = getCellBackgroundColor(format);
    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
    }
    const patternSpec = fillPatternRenderSpec(fill.patternKind ?? fill.pattern);
    const patternKind = patternSpec.kind;
    if (!patternKind || patternKind === 'none' || patternKind === 'solid') return;
    const foreground = String(fill.patternColor || fill.color || fill.foregroundColor || '#6b7280');
    const { step, alpha } = patternSpec;
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
    ctx.clip();
    ctx.strokeStyle = foreground;
    ctx.lineWidth = 1;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    const drawDiagonal = () => {
        for (let offset = -Math.ceil(h); offset < Math.ceil(w); offset += step) {
            ctx.moveTo(Math.floor(x + offset), Math.floor(y + h));
            ctx.lineTo(Math.floor(x + offset + h), Math.floor(y));
        }
    };
    const drawReverseDiagonal = () => {
        for (let offset = 0; offset < Math.ceil(w + h); offset += step) {
            ctx.moveTo(Math.floor(x + offset), Math.floor(y));
            ctx.lineTo(Math.floor(x + offset - h), Math.floor(y + h));
        }
    };
    const drawHorizontal = () => {
        for (let lineY = y + step; lineY < y + h; lineY += step) {
            ctx.moveTo(Math.floor(x), Math.floor(lineY));
            ctx.lineTo(Math.floor(x + w), Math.floor(lineY));
        }
    };
    const drawVertical = () => {
        for (let lineX = x + step; lineX < x + w; lineX += step) {
            ctx.moveTo(Math.floor(lineX), Math.floor(y));
            ctx.lineTo(Math.floor(lineX), Math.floor(y + h));
        }
    };
    switch (patternKind) {
        case 'horizontal':
            drawHorizontal();
            break;
        case 'vertical':
            drawVertical();
            break;
        case 'reverseDiagonal':
            drawReverseDiagonal();
            break;
        case 'diagonalCross':
            drawDiagonal();
            drawReverseDiagonal();
            break;
        case 'horizontalCross':
            drawHorizontal();
            drawVertical();
            break;
        default:
            drawDiagonal();
            break;
    }
    ctx.stroke();
    ctx.restore();
};

const borderWidth = (style: string | undefined): number => {
    switch (style) {
        case 'medium':
        case 'mediumDashed':
        case 'mediumDashDot':
        case 'mediumDashDotDot':
            return 2;
        case 'thick':
        case 'double':
            return 3;
        case 'hair':
            return 0.5;
        default:
            return 1;
    }
};

const borderDash = (style: string | undefined): number[] => {
    switch (style) {
        case 'dashed':
        case 'mediumDashed':
            return [4, 3];
        case 'dotted':
        case 'hair':
            return [1, 2];
        case 'dashDot':
        case 'mediumDashDot':
        case 'slantDashDot':
            return [5, 3, 1, 3];
        case 'dashDotDot':
        case 'mediumDashDotDot':
            return [5, 3, 1, 3, 1, 3];
        default:
            return [];
    }
};

const drawCellBorderSide = (
    ctx: CanvasRenderingContext2D,
    side: any,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
) => {
    if (!side || typeof side !== 'object' || side.style === 'none') return;
    const style = String(side.style || 'thin');
    ctx.save();
    ctx.strokeStyle = typeof side.color === 'string' && side.color ? side.color : '#1f2937';
    ctx.lineWidth = style === 'double' ? 1 : borderWidth(style);
    ctx.setLineDash(borderDash(style));
    const drawLine = (offsetX: number, offsetY: number) => {
        ctx.beginPath();
        ctx.moveTo(Math.floor(x1 + offsetX) + 0.5, Math.floor(y1 + offsetY) + 0.5);
        ctx.lineTo(Math.floor(x2 + offsetX) + 0.5, Math.floor(y2 + offsetY) + 0.5);
        ctx.stroke();
    };
    if (style === 'double') {
        if (Math.abs(y1 - y2) < 0.01) {
            drawLine(0, -1);
            drawLine(0, 1);
        } else if (Math.abs(x1 - x2) < 0.01) {
            drawLine(-1, 0);
            drawLine(1, 0);
        } else {
            drawLine(0, 0);
        }
    } else {
        drawLine(0, 0);
    }
    ctx.restore();
};

const drawCellBorders = (
    ctx: CanvasRenderingContext2D,
    format: any,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    const border = styleSection(format, 'border');
    drawCellBorderSide(ctx, border.top, x, y, x + w, y);
    drawCellBorderSide(ctx, border.right ?? border.insideVertical, x + w, y, x + w, y + h);
    drawCellBorderSide(ctx, border.bottom ?? border.insideHorizontal, x, y + h, x + w, y + h);
    drawCellBorderSide(ctx, border.left, x, y, x, y + h);
    drawCellBorderSide(ctx, border.diagonal, x, y + h, x + w, y);
    drawCellBorderSide(ctx, border.diagonalDown, x, y, x + w, y + h);
    drawCellBorderSide(ctx, border.diagonalUp, x, y + h, x + w, y);
};

const drawDataBarOverlay = (
    ctx: CanvasRenderingContext2D,
    dataBar: any,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    if (!dataBar) return;
    const percent = Math.max(0, Math.min(1, Number(dataBar.magnitudePercent ?? dataBar.percent) || 0));
    if (percent <= 0) return;
    const insetX = 3;
    const insetY = Math.max(3, Math.floor(h * 0.18));
    const availableWidth = Math.max(1, w - insetX * 2);
    const barWidth = Math.max(1, Math.floor(availableWidth * percent));
    const axisPercent = Number(dataBar.axisPercent);
    const hasAxis = Number.isFinite(axisPercent) && axisPercent > 0 && axisPercent < 1;
    const axisX = Math.floor(x + insetX + availableWidth * Math.max(0, Math.min(1, axisPercent)));
    const rightToLeft = ['righttoleft', 'rtl'].includes(String(dataBar.direction || '').replace(/[-_\s]/g, '').toLowerCase());
    const barX = hasAxis
        ? dataBar.negative
            ? axisX - barWidth
            : axisX
        : rightToLeft
            ? Math.floor(x + insetX + availableWidth - barWidth)
            : Math.floor(x + insetX);
    const barY = Math.floor(y + insetY);
    const barHeight = Math.max(2, Math.floor(h - insetY * 2));
    ctx.save();
    if (hasAxis) {
        ctx.fillStyle = String(dataBar.axisColor || 'rgba(31, 41, 55, 0.45)');
        ctx.globalAlpha = 1;
        ctx.fillRect(axisX, Math.floor(y + 2), 1, Math.max(2, Math.floor(h - 4)));
    }
    ctx.fillStyle = String(dataBar.negative ? (dataBar.negativeColor || '#f87171') : (dataBar.color || '#63c384'));
    ctx.globalAlpha = 0.78;
    ctx.fillRect(barX, barY, barWidth, barHeight);
    if (typeof dataBar.borderColor === 'string' && dataBar.borderColor.trim()) {
        ctx.globalAlpha = 1;
        ctx.strokeStyle = dataBar.borderColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.strokeRect(barX + 0.5, barY + 0.5, Math.max(0, barWidth - 1), Math.max(0, barHeight - 1));
    }
    ctx.restore();
};

const drawIconSetOverlay = (
    ctx: CanvasRenderingContext2D,
    iconSet: any,
    x: number,
    y: number,
    _w: number,
    h: number,
) => {
    if (!iconSet) return;
    const icon = String(iconSet.icon || '').trim();
    if (!icon) return;
    const set = String(iconSet.set || '3Arrows').trim();
    const count = Math.max(3, Math.min(5, Math.floor(Number(iconSet.count) || (set.startsWith('5') ? 5 : set.startsWith('4') ? 4 : 3))));
    const level = Math.max(0, Math.min(count - 1, Math.floor(Number(iconSet.level) || 0)));
    const centerX = Math.floor(x + 10);
    const centerY = Math.floor(y + h / 2);
    ctx.save();
    ctx.lineWidth = 2;

    if (/TrafficLights|Quarters/i.test(set)) {
        const ratio = count <= 1 ? 1 : level / (count - 1);
        ctx.fillStyle = ratio >= 0.75
            ? '#107c41'
            : ratio >= 0.5
                ? '#f2c744'
                : ratio >= 0.25
                    ? '#f97316'
                    : '#c2410c';
        ctx.strokeStyle = 'rgba(31, 41, 55, 0.32)';
        ctx.beginPath();
        ctx.arc(centerX, centerY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        return;
    }

    if (/Symbols/i.test(set)) {
        if (icon === 'greenUp') {
            ctx.strokeStyle = '#107c41';
            ctx.beginPath();
            ctx.moveTo(centerX - 5, centerY);
            ctx.lineTo(centerX - 1, centerY + 4);
            ctx.lineTo(centerX + 6, centerY - 5);
            ctx.stroke();
        } else if (icon === 'yellowSide') {
            ctx.strokeStyle = '#b7791f';
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - 6);
            ctx.lineTo(centerX, centerY + 1);
            ctx.stroke();
            ctx.fillStyle = '#b7791f';
            ctx.beginPath();
            ctx.arc(centerX, centerY + 5, 1.5, 0, Math.PI * 2);
            ctx.fill();
        } else {
            ctx.strokeStyle = '#c2410c';
            ctx.beginPath();
            ctx.moveTo(centerX - 4, centerY - 4);
            ctx.lineTo(centerX + 4, centerY + 4);
            ctx.moveTo(centerX + 4, centerY - 4);
            ctx.lineTo(centerX - 4, centerY + 4);
            ctx.stroke();
        }
        ctx.restore();
        return;
    }

    if (icon === 'greenUp') {
        ctx.strokeStyle = '#107c41';
        ctx.beginPath();
        ctx.moveTo(centerX, centerY + 5);
        ctx.lineTo(centerX, centerY - 5);
        ctx.moveTo(centerX - 4, centerY - 1);
        ctx.lineTo(centerX, centerY - 5);
        ctx.lineTo(centerX + 4, centerY - 1);
        ctx.stroke();
    } else if (icon === 'lightGreenUpRight' || icon === 'yellowUpRight') {
        ctx.strokeStyle = icon === 'lightGreenUpRight' ? '#6aa84f' : '#b7791f';
        ctx.beginPath();
        ctx.moveTo(centerX - 4, centerY + 4);
        ctx.lineTo(centerX + 5, centerY - 5);
        ctx.moveTo(centerX - 1, centerY - 5);
        ctx.lineTo(centerX + 5, centerY - 5);
        ctx.lineTo(centerX + 5, centerY + 1);
        ctx.stroke();
    } else if (icon === 'yellowSide') {
        ctx.strokeStyle = '#b7791f';
        ctx.beginPath();
        ctx.moveTo(centerX - 5, centerY);
        ctx.lineTo(centerX + 5, centerY);
        ctx.moveTo(centerX + 1, centerY - 4);
        ctx.lineTo(centerX + 5, centerY);
        ctx.lineTo(centerX + 1, centerY + 4);
        ctx.stroke();
    } else if (icon === 'orangeDownRight') {
        ctx.strokeStyle = '#f97316';
        ctx.beginPath();
        ctx.moveTo(centerX - 4, centerY - 4);
        ctx.lineTo(centerX + 5, centerY + 5);
        ctx.moveTo(centerX + 5, centerY - 1);
        ctx.lineTo(centerX + 5, centerY + 5);
        ctx.lineTo(centerX - 1, centerY + 5);
        ctx.stroke();
    } else {
        ctx.strokeStyle = '#c2410c';
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - 5);
        ctx.lineTo(centerX, centerY + 5);
        ctx.moveTo(centerX - 4, centerY + 1);
        ctx.lineTo(centerX, centerY + 5);
        ctx.lineTo(centerX + 4, centerY + 1);
        ctx.stroke();
    }
    ctx.restore();
};

const drawConditionalFormatOverlays = (
    ctx: CanvasRenderingContext2D,
    format: any,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    drawDataBarOverlay(ctx, conditionalSection(format, 'dataBar'), x, y, w, h);
    drawIconSetOverlay(ctx, conditionalSection(format, 'iconSet'), x, y, w, h);
};

const objectSection = (value: any, key: string): any => (
    value?.[key] && typeof value[key] === 'object' && !Array.isArray(value[key])
        ? value[key]
        : null
);

const sparklineColor = (colors: any, key: string, fallback: string): string => {
    const raw = colors?.[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (raw && typeof raw === 'object') {
        const rgb = typeof raw.rgb === 'string' ? raw.rgb.trim() : '';
        if (/^[0-9a-f]{8}$/i.test(rgb)) return `#${rgb.slice(2)}`;
        if (/^#[0-9a-f]{6}$/i.test(rgb)) return rgb;
    }
    return fallback;
};

const drawSparklineOverlay = (
    ctx: CanvasRenderingContext2D,
    metadata: CellMetadataProjection | undefined,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    const sparkline = objectSection(metadata?.metadata, 'sparkline');
    if (!sparkline || sparkline.renderable === false) return;
    const points = (Array.isArray(sparkline.points) ? sparkline.points : []).map((point: any, index: number) => {
        const value = Number(point?.value);
        const px = Number(point?.x);
        return {
            value: Number.isFinite(value) ? value : null,
            x: Number.isFinite(px) ? px : index,
            negative: point?.negative === true,
            first: point?.first === true,
            last: point?.last === true,
            high: point?.high === true,
            low: point?.low === true,
        };
    });
    if (points.length === 0) return;
    const scale = objectSection(sparkline, 'scale') ?? {};
    const min = Number.isFinite(Number(scale.min)) ? Number(scale.min) : 0;
    const max = Number.isFinite(Number(scale.max)) && Number(scale.max) !== min ? Number(scale.max) : min + 1;
    const xs = points.map((point: { x: number }) => point.x).filter(Number.isFinite);
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length && Math.max(...xs) !== minX ? Math.max(...xs) : minX + Math.max(1, points.length - 1);
    const insetX = 4;
    const insetY = 3;
    const plotX = x + insetX;
    const plotY = y + insetY;
    const plotW = Math.max(1, w - insetX * 2);
    const plotH = Math.max(1, h - insetY * 2);
    const rightToLeft = sparkline.rightToLeft === true;
    const colors = objectSection(sparkline, 'colors') ?? {};
    const seriesColor = sparklineColor(colors, 'series', '#107c41');
    const negativeColor = sparklineColor(colors, 'negative', '#c2410c');
    const axisColor = sparklineColor(colors, 'axis', 'rgba(31, 41, 55, 0.45)');
    const toX = (point: { x: number }) => {
        const t = maxX === minX ? 0.5 : (point.x - minX) / (maxX - minX);
        return plotX + (rightToLeft ? 1 - t : t) * plotW;
    };
    const toY = (value: number) => plotY + (1 - ((value - min) / (max - min))) * plotH;
    const zeroY = Math.max(plotY, Math.min(plotY + plotH, toY(0)));
    const type = String(sparkline.type || 'line');
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
    ctx.clip();
    if (sparkline.displayXAxis === true) {
        ctx.strokeStyle = axisColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.floor(plotX), Math.floor(zeroY) + 0.5);
        ctx.lineTo(Math.floor(plotX + plotW), Math.floor(zeroY) + 0.5);
        ctx.stroke();
    }
    if (type === 'column' || type === 'winLoss') {
        const slot = plotW / Math.max(1, points.length);
        const barW = Math.max(1, Math.floor(slot * 0.62));
        points.forEach((point: any, index: number) => {
            if (point.value == null) return;
            const value = type === 'winLoss' ? Math.sign(point.value) : point.value;
            if (type === 'winLoss' && value === 0) return;
            const center = rightToLeft ? plotX + plotW - (index + 0.5) * slot : plotX + (index + 0.5) * slot;
            const topY = type === 'winLoss' ? (value > 0 ? plotY : zeroY) : Math.min(zeroY, toY(value));
            const bottomY = type === 'winLoss' ? (value > 0 ? zeroY : plotY + plotH) : Math.max(zeroY, toY(value));
            ctx.fillStyle = point.negative ? negativeColor : seriesColor;
            ctx.globalAlpha = 0.9;
            ctx.fillRect(Math.floor(center - barW / 2), Math.floor(topY), barW, Math.max(1, Math.floor(bottomY - topY)));
        });
        ctx.restore();
        return;
    }
    ctx.strokeStyle = seriesColor;
    ctx.lineWidth = Math.max(1, Math.min(4, Number(sparkline.lineWeight) || 1.25));
    ctx.setLineDash([]);
    ctx.beginPath();
    let open = false;
    for (const point of points) {
        if (point.value == null) {
            if (String(sparkline.emptyPolicy || 'gap') === 'gap') open = false;
            continue;
        }
        const px = toX(point);
        const py = toY(point.value);
        if (!open) {
            ctx.moveTo(px, py);
            open = true;
        } else {
            ctx.lineTo(px, py);
        }
    }
    ctx.stroke();
    const markers = objectSection(sparkline, 'markers') ?? {};
    points.forEach((point: any) => {
        if (point.value == null) return;
        const shouldMark = markers.markers === true
            || (point.first && markers.first)
            || (point.last && markers.last)
            || (point.high && markers.high)
            || (point.low && markers.low)
            || (point.negative && markers.negative);
        if (!shouldMark) return;
        const markerKey = point.negative ? 'negative' : point.first ? 'first' : point.last ? 'last' : point.high ? 'high' : point.low ? 'low' : 'markers';
        ctx.fillStyle = sparklineColor(colors, markerKey, point.negative ? negativeColor : seriesColor);
        ctx.beginPath();
        ctx.arc(toX(point), toY(point.value), 2.4, 0, Math.PI * 2);
        ctx.fill();
    });
    ctx.restore();
};

type CellMetadataProjection = {
    validationId?: string;
    comment?: string;
    hyperlink?: string;
    protection?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
};

type CellProtectionVisualState = 'explicit-locked' | 'explicit-unlocked' | 'sheet-locked' | null;

const getCellProtectionVisualState = (
    protection: Record<string, unknown> | undefined,
    sheetProtected: boolean,
): CellProtectionVisualState => {
    if (protection) {
        return Object.prototype.hasOwnProperty.call(protection, 'locked') && protection.locked === false
            ? 'explicit-unlocked'
            : 'explicit-locked';
    }
    return sheetProtected ? 'sheet-locked' : null;
};

const drawCellProtectionIndicator = (
    ctx: CanvasRenderingContext2D,
    state: CellProtectionVisualState,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    if (!state) return;
    if (state === 'explicit-locked') {
        ctx.strokeStyle = 'rgba(16, 124, 65, 0.38)';
        ctx.lineWidth = 1;
        ctx.strokeRect(Math.floor(x + 2.5), Math.floor(y + 2.5), Math.max(1, Math.floor(w - 5)), Math.max(1, Math.floor(h - 5)));
        return;
    }
    if (state === 'explicit-unlocked') {
        ctx.fillStyle = 'rgba(16, 124, 65, 0.14)';
        ctx.fillRect(Math.floor(x + w - 10), Math.floor(y + 2), 7, 7);
        ctx.strokeStyle = 'rgba(16, 124, 65, 0.62)';
        ctx.lineWidth = 1;
        ctx.strokeRect(Math.floor(x + w - 10) + 0.5, Math.floor(y + 2) + 0.5, 7, 7);
        return;
    }
    ctx.strokeStyle = 'rgba(180, 83, 9, 0.20)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.floor(x + w - 10), Math.floor(y + 2.5));
    ctx.lineTo(Math.floor(x + w - 3), Math.floor(y + 2.5));
    ctx.lineTo(Math.floor(x + w - 3), Math.floor(y + 9.5));
    ctx.stroke();
};

const drawCellMetadataDecorations = (
    ctx: CanvasRenderingContext2D,
    metadata: CellMetadataProjection | undefined,
    sheetProtected: boolean,
    x: number,
    y: number,
    w: number,
    h: number,
) => {
    if (metadata?.comment) {
        ctx.fillStyle = '#107c41';
        ctx.beginPath();
        ctx.moveTo(Math.floor(x + w - 8), Math.floor(y));
        ctx.lineTo(Math.floor(x + w), Math.floor(y));
        ctx.lineTo(Math.floor(x + w), Math.floor(y + 8));
        ctx.closePath();
        ctx.fill();
    }
    if (metadata?.hyperlink) {
        ctx.strokeStyle = '#107c41';
        ctx.lineWidth = 1.5;
        const underlineY = Math.floor(y + h - 4);
        ctx.beginPath();
        ctx.moveTo(Math.floor(x + 5), underlineY);
        ctx.lineTo(Math.floor(x + Math.min(w - 5, 34)), underlineY);
        ctx.stroke();
    }
    if (metadata?.validationId) {
        ctx.fillStyle = '#107c41';
        ctx.beginPath();
        ctx.arc(Math.floor(x + w - 6), Math.floor(y + h - 6), 2.5, 0, Math.PI * 2);
        ctx.fill();
    }
    drawCellProtectionIndicator(
        ctx,
        getCellProtectionVisualState(metadata?.protection, sheetProtected),
        x,
        y,
        w,
        h,
    );
};

const dimensionSpan = (
    start: number,
    count: number,
    limit: number,
    getSize: (index: number) => number,
): number => {
    let total = 0;
    const end = Math.min(limit, Math.max(start, start + Math.max(0, count)));
    for (let index = Math.max(0, start); index < end; index++) {
        total += getSize(index);
    }
    return total;
};

const dimensionDistance = (
    start: number,
    end: number,
    getSize: (index: number) => number,
): number => {
    let total = 0;
    for (let index = Math.max(0, start); index < Math.max(start, end); index++) {
        total += getSize(index);
    }
    return total;
};

export class CanvasRenderer {
    private ctx: CanvasRenderingContext2D | null = null;
    private canvas: HTMLCanvasElement;
    private dpr: number = 1;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.dpr = window.devicePixelRatio || 1;
    }

    resize(width: number, height: number) {
        this.dpr = window.devicePixelRatio || 1;
        // Use integer backing-store sizes to avoid fractional pixel blur.
        this.canvas.width = Math.max(1, Math.floor(width * this.dpr));
        this.canvas.height = Math.max(1, Math.floor(height * this.dpr));
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
        if (this.ctx) {
            // IMPORTANT: do not accumulate scaling (ctx.scale composes with the existing transform).
            // Always reset transform then apply DPR scaling.
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            // We mostly render crisp lines/text; avoid extra filtering work.
            this.ctx.imageSmoothingEnabled = false;
            this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        }
    }

    render(
        viewState: ViewState,
        getColWidth: (i: number) => number,
        getRowHeight: (i: number) => number,
        getCellFormat: (col: number, row: number) => Record<string, unknown> | undefined,
        getCellMetadata: (col: number, row: number) => CellMetadataProjection | undefined,
        colCount: number,
        rowCount: number
    ) {
        if (!this.ctx) return;

        const { scrollX, scrollY, viewportWidth, viewportHeight, selection } = viewState;
        const frozenRowsTop = Math.max(0, Math.floor(viewState.frozenRowsTop ?? 0));
        const frozenColsLeft = Math.max(0, Math.floor(viewState.frozenColsLeft ?? 0));
        const gridlinesVisible = viewState.gridlinesVisible !== false;
        const sheetProtected = viewState.sheetProtection?.enabled === true;
        const ctx = this.ctx;
        const paintCellBackgroundLayer = (
            col: number,
            row: number,
            x: number,
            y: number,
            w: number,
            h: number,
            passStartCol: number,
            passStartRow: number,
        ) => {
            const format = getCellFormat(col, row);
            const placement = mergedPlacement(format);
            if (!placement) {
                const metadata = getCellMetadata(col, row);
                drawCellFill(ctx, format, x, y, w, h);
                drawConditionalFormatOverlays(ctx, format, x, y, w, h);
                drawSparklineOverlay(ctx, metadata, x, y, w, h);
                drawCellBorders(ctx, format, x, y, w, h);
                drawCellMetadataDecorations(ctx, metadata, sheetProtected, x, y, w, h);
                return;
            }

            const rootCol = Math.max(0, col - placement.colOffset);
            const rootRow = Math.max(0, row - placement.rowOffset);
            if (col !== Math.max(rootCol, passStartCol) || row !== Math.max(rootRow, passStartRow)) {
                return;
            }

            const rootX = x - dimensionDistance(rootCol, col, getColWidth);
            const rootY = y - dimensionDistance(rootRow, row, getRowHeight);
            const mergedWidth = dimensionSpan(rootCol, placement.colSpan, colCount, getColWidth);
            const mergedHeight = dimensionSpan(rootRow, placement.rowSpan, rowCount, getRowHeight);
            const rootFormat = getCellFormat(rootCol, rootRow) ?? format;
            const metadata = getCellMetadata(rootCol, rootRow);
            drawCellFill(ctx, rootFormat, rootX, rootY, mergedWidth || w, mergedHeight || h);
            drawConditionalFormatOverlays(ctx, rootFormat, rootX, rootY, mergedWidth || w, mergedHeight || h);
            drawSparklineOverlay(ctx, metadata, rootX, rootY, mergedWidth || w, mergedHeight || h);
            drawCellBorders(ctx, rootFormat, rootX, rootY, mergedWidth || w, mergedHeight || h);
            drawCellMetadataDecorations(ctx, metadata, sheetProtected, rootX, rootY, mergedWidth || w, mergedHeight || h);
        };

        // Clear
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, viewportWidth, viewportHeight);

        // Prefer precomputed layout info from controller. Fall back to O(N) scans if absent.
        let startCol = viewState.startCol ?? 0;
        let startRow = viewState.startRow ?? 0;
        let startX = viewState.startX ?? 0;
        let startY = viewState.startY ?? 0;
        const endColHint = viewState.endCol;
        const endRowHint = viewState.endRow;

        if (
            viewState.startCol === undefined ||
            viewState.startRow === undefined ||
            viewState.startX === undefined ||
            viewState.startY === undefined
        ) {
            // Helper to find visible range start (slow path)
            let currentOffset = 0;
            for (let c = 0; c < colCount; c++) {
                const w = getColWidth(c);
                if (currentOffset + w > scrollX) {
                    startCol = c;
                    startX = currentOffset - scrollX;
                    break;
                }
                currentOffset += w;
            }

            currentOffset = 0;
            for (let r = 0; r < rowCount; r++) {
                const h = getRowHeight(r);
                if (currentOffset + h > scrollY) {
                    startRow = r;
                    startY = currentOffset - scrollY;
                    break;
                }
                currentOffset += h;
            }
        }

        // Compute frozen header height (in pixels)
        let frozenHeight = 0;
        if (frozenRowsTop > 0) {
            for (let r = 0; r < Math.min(frozenRowsTop, rowCount); r++) {
                frozenHeight += getRowHeight(r);
            }
        }
        let frozenWidth = 0;
        if (frozenColsLeft > 0) {
            for (let c = 0; c < Math.min(frozenColsLeft, colCount); c++) {
                frozenWidth += getColWidth(c);
            }
        }

        // Clip the scrolling pass so content never renders under the frozen header area.
        // (Without this, scrollY > 0 can draw the first data row behind the header; if the header has no fill,
        // it looks like header text overlaps body text.)
        ctx.save();
        if ((frozenRowsTop > 0 && frozenHeight > 0) || (frozenColsLeft > 0 && frozenWidth > 0)) {
            ctx.beginPath();
            const clipX = Math.min(frozenWidth, viewportWidth);
            const clipY = Math.min(frozenHeight, viewportHeight);
            ctx.rect(clipX, clipY, Math.max(0, viewportWidth - clipX), Math.max(0, viewportHeight - clipY));
            ctx.clip();
        }

        // Debug Logging

        // Draw Background Fills (Visible Only)
        let bgX = startX;
        let bgStartCol = startCol;
        if (frozenColsLeft > 0 && bgStartCol < frozenColsLeft) {
            for (let c = bgStartCol; c < frozenColsLeft && c < colCount; c++) {
                bgX += getColWidth(c);
            }
            bgStartCol = frozenColsLeft;
        }
        for (let c = bgStartCol; c < colCount; c++) {
            if (bgX >= viewportWidth) break; // clipped right
            const w = getColWidth(c);

            // Skip frozen rows in the scrolling pass; we'll draw them pinned afterwards.
            let bgY = startY;
            let r0 = startRow;
            if (frozenRowsTop > 0 && r0 < frozenRowsTop) {
                for (let r = r0; r < frozenRowsTop && r < rowCount; r++) {
                    bgY += getRowHeight(r);
                }
                r0 = frozenRowsTop;
            }

            for (let r = r0; r < rowCount; r++) {
                if (bgY >= viewportHeight) break; // clipped bottom
                const h = getRowHeight(r);

                paintCellBackgroundLayer(c, r, bgX, bgY, w, h, bgStartCol, r0);
                bgY += h;
            }
            bgX += w;
        }

        if (gridlinesVisible) {
            // Draw Grid
            ctx.strokeStyle = '#e0e0e0';
            ctx.lineWidth = 1;
            ctx.beginPath();

            // Horizontal Lines
            // Skip frozen rows in the scrolling grid pass.
            let currentY = startY;
            let gridStartRow = startRow;
            if (frozenRowsTop > 0 && gridStartRow < frozenRowsTop) {
                for (let r = gridStartRow; r < frozenRowsTop && r < rowCount; r++) {
                    currentY += getRowHeight(r);
                }
                gridStartRow = frozenRowsTop;
            }

            const visibleRowSegments: Array<{ row: number; y: number; h: number }> = [];

            // We iterate from startRow.
            for (let r = gridStartRow; r < rowCount; r++) {
                if (currentY > viewportHeight) {
                    break;
                }
                const h = getRowHeight(r);
                visibleRowSegments.push({ row: r, y: currentY, h });
                const y = currentY + h; // Bottom edge of row

                let segmentX = startX;
                let segmentStartCol = startCol;
                if (frozenColsLeft > 0 && segmentStartCol < frozenColsLeft) {
                    for (let c = segmentStartCol; c < frozenColsLeft && c < colCount; c++) {
                        segmentX += getColWidth(c);
                    }
                    segmentStartCol = frozenColsLeft;
                }
                for (let c = segmentStartCol; c < colCount; c++) {
                    if (segmentX >= viewportWidth) break;
                    const w = getColWidth(c);
                    const topFmt = getCellFormat(c, r);
                    const bottomFmt = (r + 1) < rowCount ? getCellFormat(c, r + 1) : undefined;
                    if (!sharesMergeRoot(c, r, topFmt, c, r + 1, bottomFmt)) {
                        ctx.moveTo(Math.floor(segmentX), Math.floor(y) - 0.5);
                        ctx.lineTo(Math.floor(segmentX + w), Math.floor(y) - 0.5);
                    }
                    segmentX += w;
                }

                currentY += h;
            }

            // If simple full visible grid, lines should extend to bottom of viewport?
            // No, visual grid usually stops at the last row if data ends there.
            // But if rows extend beyond viewport (which they do), maxDrawnY >= viewportHeight.

            // Wait, if maxDrawnY < viewportHeight (e.g. only 5 rows), we want vertical lines to stop at maxDrawnY.
            // If maxDrawnY > viewportHeight, we want to stop at viewportHeight.
            const verticalLineLimit = (currentY < viewportHeight) ? currentY : viewportHeight;
            const verticalLineTop = frozenRowsTop > 0 ? Math.min(frozenHeight, viewportHeight) : 0;

            // Vertical Lines
            let currentX = startX;
            let verticalStartCol = startCol;
            if (frozenColsLeft > 0 && verticalStartCol < frozenColsLeft) {
                for (let c = verticalStartCol; c < frozenColsLeft && c < colCount; c++) {
                    currentX += getColWidth(c);
                }
                verticalStartCol = frozenColsLeft;
            }
            for (let c = verticalStartCol; c < colCount; c++) {
                if (currentX > viewportWidth) {
                    break;
                }
                const w = getColWidth(c);
                const x = currentX + w; // Right edge of col

                for (const rowSeg of visibleRowSegments) {
                    const leftFmt = getCellFormat(c, rowSeg.row);
                    const rightFmt = (c + 1) < colCount ? getCellFormat(c + 1, rowSeg.row) : undefined;
                    if (!sharesMergeRoot(c, rowSeg.row, leftFmt, c + 1, rowSeg.row, rightFmt)) {
                        const y0 = Math.max(verticalLineTop, rowSeg.y);
                        const y1 = Math.min(verticalLineLimit, rowSeg.y + rowSeg.h);
                        if (y1 > y0) {
                            ctx.moveTo(Math.floor(x) - 0.5, Math.floor(y0));
                            ctx.lineTo(Math.floor(x) - 0.5, Math.floor(y1));
                        }
                    }
                }

                currentX += w;
            }

            ctx.stroke();
        }

        // End scrolling-pass clip (frozen header is rendered un-clipped on top).
        ctx.restore();

        // --- Frozen header rows (pinned at top) ---
        if (frozenRowsTop > 0 && frozenHeight > 0) {
            // Always paint an opaque header background so body never bleeds through.
            ctx.fillStyle = '#f6f1ea';
            ctx.fillRect(0, 0, viewportWidth, Math.min(frozenHeight, viewportHeight));

            ctx.save();
            if (frozenColsLeft > 0 && frozenWidth > 0) {
                ctx.beginPath();
                const clipX = Math.min(frozenWidth, viewportWidth);
                ctx.rect(clipX, 0, Math.max(0, viewportWidth - clipX), Math.min(frozenHeight, viewportHeight));
                ctx.clip();
            }

            // Draw frozen background fills on top (covers scrolled content behind)
            let fx = startX;
            for (let c = startCol; c < colCount; c++) {
                if (fx >= viewportWidth) break;
                const w = getColWidth(c);

                let fy = 0;
                for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                    const h = getRowHeight(r);
                    paintCellBackgroundLayer(c, r, fx, fy, w, h, startCol, 0);
                    fy += h;
                }
                fx += w;
            }

            if (gridlinesVisible) {
                // Draw header grid lines (over the background we just painted)
                ctx.strokeStyle = '#e0e0e0';
                ctx.lineWidth = 1;
                ctx.beginPath();

                // Horizontal line(s) at the bottom of each frozen row
                let fy = 0;
                for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                    const h = getRowHeight(r);
                    fy += h;
                    const y = fy;
                    let hx = startX;
                    for (let c = startCol; c < colCount; c++) {
                        if (hx >= viewportWidth) break;
                        const w = getColWidth(c);
                        const topFmt = getCellFormat(c, r);
                        const bottomFmt = (r + 1) < frozenRowsTop ? getCellFormat(c, r + 1) : undefined;
                        if (!sharesMergeRoot(c, r, topFmt, c, r + 1, bottomFmt)) {
                            ctx.moveTo(Math.floor(hx), Math.floor(y) - 0.5);
                            ctx.lineTo(Math.floor(hx + w), Math.floor(y) - 0.5);
                        }
                        hx += w;
                    }
                }

                // Vertical lines across frozen header height
                let fx2 = startX;
                for (let c = startCol; c < colCount; c++) {
                    if (fx2 > viewportWidth) break;
                    const w = getColWidth(c);
                    const x = fx2 + w;
                    let vy = 0;
                    for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                        const h = getRowHeight(r);
                        const leftFmt = getCellFormat(c, r);
                        const rightFmt = (c + 1) < colCount ? getCellFormat(c + 1, r) : undefined;
                        if (!sharesMergeRoot(c, r, leftFmt, c + 1, r, rightFmt)) {
                            ctx.moveTo(Math.floor(x) - 0.5, Math.floor(vy));
                            ctx.lineTo(Math.floor(x) - 0.5, Math.floor(vy + h));
                        }
                        vy += h;
                    }
                    fx2 += w;
                }

                ctx.stroke();
            }

            ctx.restore();
        }

        // --- Frozen left columns (pinned at left) ---
        if (frozenColsLeft > 0 && frozenWidth > 0) {
            const paneWidth = Math.min(frozenWidth, viewportWidth);
            const paneBodyTop = Math.min(frozenHeight, viewportHeight);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, paneBodyTop, paneWidth, Math.max(0, viewportHeight - paneBodyTop));
            if (frozenRowsTop > 0 && frozenHeight > 0) {
                ctx.fillStyle = '#f6f1ea';
                ctx.fillRect(0, 0, paneWidth, Math.min(frozenHeight, viewportHeight));
            }

            let leftX = 0;
            for (let c = 0; c < frozenColsLeft && c < colCount; c++) {
                const w = getColWidth(c);
                let topY = 0;
                for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                    const h = getRowHeight(r);
                    paintCellBackgroundLayer(c, r, leftX, topY, w, h, 0, 0);
                    topY += h;
                }

                ctx.save();
                if (paneBodyTop > 0) {
                    ctx.beginPath();
                    ctx.rect(0, paneBodyTop, paneWidth, Math.max(0, viewportHeight - paneBodyTop));
                    ctx.clip();
                }
                let bodyY = startY;
                let bodyStartRow = startRow;
                if (frozenRowsTop > 0 && bodyStartRow < frozenRowsTop) {
                    for (let r = bodyStartRow; r < frozenRowsTop && r < rowCount; r++) {
                        bodyY += getRowHeight(r);
                    }
                    bodyStartRow = frozenRowsTop;
                }
                for (let r = bodyStartRow; r < rowCount; r++) {
                    if (bodyY >= viewportHeight) break;
                    const h = getRowHeight(r);
                    paintCellBackgroundLayer(c, r, leftX, bodyY, w, h, 0, bodyStartRow);
                    bodyY += h;
                }
                ctx.restore();
                leftX += w;
            }

            if (gridlinesVisible) {
                ctx.strokeStyle = '#e0e0e0';
                ctx.lineWidth = 1;
                ctx.beginPath();

                let gridX = 0;
                for (let c = 0; c < frozenColsLeft && c < colCount; c++) {
                    const w = getColWidth(c);
                    const x = gridX + w;
                    let vy = 0;
                    for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                        const h = getRowHeight(r);
                        const leftFmt = getCellFormat(c, r);
                        const rightFmt = (c + 1) < colCount ? getCellFormat(c + 1, r) : undefined;
                        if (!sharesMergeRoot(c, r, leftFmt, c + 1, r, rightFmt)) {
                            ctx.moveTo(Math.floor(x) - 0.5, Math.floor(vy));
                            ctx.lineTo(Math.floor(x) - 0.5, Math.floor(vy + h));
                        }
                        vy += h;
                    }
                    gridX += w;
                }

                let topY = 0;
                for (let r = 0; r < frozenRowsTop && r < rowCount; r++) {
                    const h = getRowHeight(r);
                    topY += h;
                    let hx = 0;
                    for (let c = 0; c < frozenColsLeft && c < colCount; c++) {
                        const w = getColWidth(c);
                        const topFmt = getCellFormat(c, r);
                        const bottomFmt = (r + 1) < rowCount ? getCellFormat(c, r + 1) : undefined;
                        if (!sharesMergeRoot(c, r, topFmt, c, r + 1, bottomFmt)) {
                            ctx.moveTo(Math.floor(hx), Math.floor(topY) - 0.5);
                            ctx.lineTo(Math.floor(hx + w), Math.floor(topY) - 0.5);
                        }
                        hx += w;
                    }
                }
                ctx.stroke();
                ctx.beginPath();

                ctx.save();
                if (paneBodyTop > 0) {
                    ctx.beginPath();
                    ctx.rect(0, paneBodyTop, paneWidth, Math.max(0, viewportHeight - paneBodyTop));
                    ctx.clip();
                }
                {
                    let bodyY = startY;
                    let bodyStartRow = startRow;
                    if (frozenRowsTop > 0 && bodyStartRow < frozenRowsTop) {
                        for (let r = bodyStartRow; r < frozenRowsTop && r < rowCount; r++) {
                            bodyY += getRowHeight(r);
                        }
                        bodyStartRow = frozenRowsTop;
                    }
                    for (let r = bodyStartRow; r < rowCount; r++) {
                        if (bodyY > viewportHeight) break;
                        const h = getRowHeight(r);
                        const y = bodyY + h;
                        let hx = 0;
                        for (let c = 0; c < frozenColsLeft && c < colCount; c++) {
                            const w = getColWidth(c);
                            const topFmt = getCellFormat(c, r);
                            const bottomFmt = (r + 1) < rowCount ? getCellFormat(c, r + 1) : undefined;
                            if (!sharesMergeRoot(c, r, topFmt, c, r + 1, bottomFmt)) {
                                ctx.moveTo(Math.floor(hx), Math.floor(y) - 0.5);
                                ctx.lineTo(Math.floor(hx + w), Math.floor(y) - 0.5);
                            }
                            hx += w;
                        }
                        bodyY += h;
                    }
                }
                ctx.stroke();
                ctx.restore();

                ctx.beginPath();
                let bodyGridX = 0;
                for (let c = 0; c < frozenColsLeft && c < colCount; c++) {
                    const w = getColWidth(c);
                    const x = bodyGridX + w;
                    let bodyY = startY;
                    let bodyStartRow = startRow;
                    if (frozenRowsTop > 0 && bodyStartRow < frozenRowsTop) {
                        for (let r = bodyStartRow; r < frozenRowsTop && r < rowCount; r++) {
                            bodyY += getRowHeight(r);
                        }
                        bodyStartRow = frozenRowsTop;
                    }
                    for (let r = bodyStartRow; r < rowCount; r++) {
                        if (bodyY > viewportHeight) break;
                        const h = getRowHeight(r);
                        const leftFmt = getCellFormat(c, r);
                        const rightFmt = (c + 1) < colCount ? getCellFormat(c + 1, r) : undefined;
                        if (!sharesMergeRoot(c, r, leftFmt, c + 1, r, rightFmt)) {
                            const y0 = Math.max(paneBodyTop, bodyY);
                            const y1 = Math.min(viewportHeight, bodyY + h);
                            if (y1 > y0) {
                                ctx.moveTo(Math.floor(x) - 0.5, Math.floor(y0));
                                ctx.lineTo(Math.floor(x) - 0.5, Math.floor(y1));
                            }
                        }
                        bodyY += h;
                    }
                    bodyGridX += w;
                }
                ctx.stroke();
            }

            ctx.strokeStyle = '#b7c3d0';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(Math.floor(paneWidth) - 0.5, 0);
            ctx.lineTo(Math.floor(paneWidth) - 0.5, viewportHeight);
            if (frozenRowsTop > 0 && frozenHeight > 0) {
                const paneHeight = Math.min(frozenHeight, viewportHeight);
                ctx.moveTo(0, Math.floor(paneHeight) - 0.5);
                ctx.lineTo(viewportWidth, Math.floor(paneHeight) - 0.5);
            }
            ctx.stroke();
        }

        if (viewState.dragPreviewScreenRect && viewState.dragPreviewMode) {
            const rect = viewState.dragPreviewScreenRect;
            drawRectIfVisible(ctx, rect, viewportWidth, viewportHeight, () => {
                ctx.save();
                ctx.strokeStyle = viewState.dragPreviewMode === 'copy' ? '#107c41' : '#374151';
                ctx.fillStyle = viewState.dragPreviewMode === 'copy'
                    ? 'rgba(16, 124, 65, 0.08)'
                    : viewState.dragPreviewMode === 'fill'
                        ? 'rgba(16, 124, 65, 0.06)'
                        : 'rgba(17, 24, 39, 0.04)';
                ctx.lineWidth = 2;
                ctx.setLineDash(viewState.dragPreviewMode === 'move' ? [6, 4] : [4, 3]);
                ctx.lineDashOffset = viewState.marqueeOffset ?? 0;
                ctx.fillRect(Math.floor(rect.x), Math.floor(rect.y), Math.ceil(rect.w), Math.ceil(rect.h));
                ctx.strokeRect(Math.floor(rect.x) + 0.5, Math.floor(rect.y) + 0.5, Math.max(0, Math.floor(rect.w) - 1), Math.max(0, Math.floor(rect.h) - 1));
                ctx.restore();
            });
        }

        // Draw Selection
        if (selection && viewState.selectionScreenRect) {
            const { x: screenX, y: screenY, w, h } = viewState.selectionScreenRect;

            // Clip check
            drawRectIfVisible(ctx, viewState.selectionScreenRect, viewportWidth, viewportHeight, () => {
                ctx.save();
                ctx.strokeStyle = '#107c41';
                ctx.lineWidth = 2;
                ctx.strokeRect(screenX, screenY, w, h);

                ctx.fillStyle = 'rgba(16, 124, 65, 0.10)';
                ctx.fillRect(screenX, screenY, w, h);

                ctx.fillStyle = '#107c41';
                ctx.fillRect(screenX + w - 4, screenY + h - 4, 6, 6);
                ctx.strokeRect(screenX + w - 4, screenY + h - 4, 6, 6);
                ctx.restore();
            });
        } else if (selection) {
            // Fallback: draw selection only when it overlaps the visible range.
            const minCol = Math.min(selection.col, selection.endCol);
            const maxCol = Math.max(selection.col, selection.endCol);
            const minRow = Math.min(selection.row, selection.endRow);
            const maxRow = Math.max(selection.row, selection.endRow);

            if (
                endColHint !== undefined &&
                endRowHint !== undefined &&
                (maxCol < startCol || minCol > endColHint || maxRow < startRow || minRow > endRowHint)
            ) {
                return;
            }
        }

        if (viewState.clipboardSelectionScreenRect && viewState.clipboardMode) {
            const rect = viewState.clipboardSelectionScreenRect;
            drawRectIfVisible(ctx, rect, viewportWidth, viewportHeight, () => {
                ctx.save();
                ctx.strokeStyle = viewState.clipboardMode === 'cut' ? '#b45309' : '#107c41';
                ctx.lineWidth = 2;
                ctx.setLineDash([4, 3]);
                ctx.lineDashOffset = viewState.marqueeOffset ?? 0;
                ctx.strokeRect(Math.floor(rect.x) + 0.5, Math.floor(rect.y) + 0.5, Math.max(0, Math.floor(rect.w) - 1), Math.max(0, Math.floor(rect.h) - 1));
                ctx.restore();
            });
        }
    }
}
